import { Injectable } from '@angular/core';
import {
  Auth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  authState,
  User,
  AuthError,
} from '@angular/fire/auth';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { OnboardingApiService } from './onboarding-api.service';
import { StateManagementService } from './state-management.service';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  constructor(
    private auth: Auth,
    private api: OnboardingApiService,
    private stateService: StateManagementService,
    private router: Router
  ) {
    // Track Firebase Auth state changes
    authState(this.auth).subscribe(user => {
      this.currentUserSubject.next(user || null);
      this.isAuthenticatedSubject.next(!!user);
    });
  }

  /**
   * Register a new organization with email/password
   * @param orgName Name of the organization
   * @param email Admin email address
   * @param password Password for authentication
   * @returns Organization ID
   */
  async registerOrganization(orgName: string, email: string, password: string, legalAcceptance?: any): Promise<string> {
    try {
      // Create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(
        this.auth,
        email,
        password
      );

      const uid = userCredential.user.uid;

      // Build referral info from state (populated from URL params)
      const snapshot = this.stateService.snapshot;
      const referralInfo = (snapshot.referralId || snapshot.partnerId || snapshot.partnerPrefill?.referralCode)
        ? {
          referralId: snapshot.referralId || undefined,
          partnerId: snapshot.partnerId || undefined,
          referralCode: snapshot.partnerPrefill?.referralCode || undefined,
        }
        : undefined;

      // Call Cloud Function to create organization with admin email
      const response = await this.api.createOrganizationWithAuth(
        orgName,
        email,
        uid,
        legalAcceptance,
        referralInfo,
      ).toPromise();

      if (!response?.organizationId) {
        // Rollback: delete the Firebase Auth user if org creation fails
        await userCredential.user.delete();
        throw new Error('Failed to create organization');
      }

      // Sign out immediately — org is pending setup completion
      // Don't navigate here, let the component handle it
      await signOut(this.auth);

      return response.organizationId;
    } catch (error: any) {
      const message = this.getAuthErrorMessage(error);
      throw new Error(message);
    }
  }

  /**
   * Login with email/password
   */
  async login(email: string, password: string): Promise<User> {
    try {
      const userCredential = await signInWithEmailAndPassword(
        this.auth,
        email,
        password
      );
      return userCredential.user;
    } catch (error: any) {
      const message = this.getAuthErrorMessage(error);
      throw new Error(message);
    }
  }

  /**
   * Logout current user
   */
  async logout(): Promise<void> {
    try {
      await signOut(this.auth);
    } catch (error: any) {
      const message = this.getAuthErrorMessage(error);
      throw new Error(message);
    }
  }

  async sendPasswordReset(email: string): Promise<void> {
    await sendPasswordResetEmail(this.auth, email);
  }

  /**
   * Get current authenticated user
   */
  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  /**
   * Get current user's email
   */
  getCurrentUserEmail(): string | null {
    return this.currentUserSubject.value?.email || null;
  }

  /**
   * Get current user's UID
   */
  getCurrentUserUid(): string | null {
    return this.currentUserSubject.value?.uid || null;
  }

  /**
   * Get current user's ID token for API calls
   */
  async getIdToken(): Promise<string | null> {
    const user = this.currentUserSubject.value;
    if (!user) return null;

    try {
      return await user.getIdToken(true); // Force refresh
    } catch (error) {
      console.error('Failed to get ID token:', error);
      return null;
    }
  }

  /**
   * Check if user is currently authenticated
   */
  isAuthenticated(): boolean {
    return this.isAuthenticatedSubject.value;
  }

  /**
   * Convert Firebase Auth errors to user-friendly messages
   */
  private getAuthErrorMessage(error: any): string {
    if (!error) return 'An unknown error occurred';

    // Firebase AuthError
    if (error.code) {
      const errorCode = error.code as string;

      // Common auth errors
      const errorMap: { [key: string]: string } = {
        'auth/email-already-in-use': 'This email is already registered. Please log in or use a different email.',
        'auth/invalid-email': 'Please enter a valid email address.',
        'auth/weak-password': 'Password must be at least 8 characters long.',
        'auth/user-not-found': 'No account found with this email. Please register first.',
        'auth/wrong-password': 'Incorrect password. Please try again.',
        'auth/user-disabled': 'This account has been disabled.',
        'auth/too-many-requests': 'Too many failed login attempts. Please try again later.',
        'auth/operation-not-allowed': 'Email/password sign-in is not enabled.',
        'auth/network-request-failed': 'Network error. Please check your connection.',
        'auth/internal-error': 'An internal error occurred. Please try again.',
      };

      return errorMap[errorCode] || `Auth error: ${errorCode}`;
    }

    // Standard error with message
    if (error.message) {
      return error.message;
    }

    return 'An unknown error occurred';
  }
}
